import { mixOklch } from './color';

/** Default tenant primary (design `--p-base`). */
export const DEFAULT_PRIMARY = '#3949AB';

/** Static design tokens from design/project (light and dark). Primary-derived tokens come from `primaryTokens`. */
export const tokens = {
  light: {
    bg: '#F6F7FB',
    surf: '#FFFFFF',
    surf2: '#F1F2F8',
    surf3: '#E8EAF2',
    fg: '#1B1C22',
    fg2: '#4F5260',
    fg3: '#6E7180',
    line: '#E2E4EC',
    line2: '#C7CAD6',
    ok: '#1B6E3E',
    okC: '#DAF1E2',
    warn: '#7F5300',
    warnC: '#FFEFC7',
    err: '#B3261E',
    errC: '#FCE3DF',
    slate: '#475061',
    slateC: '#ECEEF2',
    scrim: 'rgba(20, 22, 30, 0.4)',
  },
  dark: {
    bg: '#111217',
    surf: '#191A20',
    surf2: '#202229',
    surf3: '#2A2C35',
    fg: '#E4E5EE',
    fg2: '#B5B8C6',
    fg3: '#8E919E',
    line: '#30323C',
    line2: '#474A57',
    ok: '#7FD9A1',
    okC: '#113A23',
    warn: '#F4C25A',
    warnC: '#3B2A00',
    err: '#FFB4AB',
    errC: '#5C1611',
    slate: '#C3C8D4',
    slateC: '#2D313A',
    scrim: 'rgba(0, 0, 0, 0.6)',
  },
  /** Fixed, non-themed colours (snackbar, tooltip). */
  inverse: { bg: '#2F3036', fg: '#F2F2F7', ok: '#7FD9A1', action: '#C5CAFF', muted: '#C7C8D0' },
} as const;

export type SchemeTokens = { [K in keyof typeof tokens.light]: string };

export interface PrimaryTokens {
  p: string;
  onP: string;
  pCont: string;
  onPCont: string;
}

/** Mirrors the design's `color-mix(in oklch, …)` definitions of --p, --on-p, --p-cont, --on-p-cont. */
export function primaryTokens(base: string): { light: PrimaryTokens; dark: PrimaryTokens } {
  return {
    light: {
      p: base.toUpperCase(),
      onP: '#FFFFFF',
      pCont: mixOklch(base, 13, '#FFFFFF'),
      onPCont: mixOklch(base, 70, '#000000'),
    },
    dark: {
      p: mixOklch(base, 42, '#FFFFFF'),
      onP: mixOklch(base, 60, '#000000'),
      pCont: mixOklch(base, 55, '#121318'),
      onPCont: mixOklch(base, 18, '#FFFFFF'),
    },
  };
}

export const radii = {
  field: 4,
  chip: 8,
  tag: 6,
  card: 12,
  dialog: 20,
  mobileCard: 16,
  sheet: 28,
  pill: 999,
} as const;

export const shadows = {
  cardHover: '0 2px 10px rgba(0,0,0,.06)',
  menu: '0 2px 6px rgba(0,0,0,.12), 0 8px 24px rgba(0,0,0,.1)',
  dialog: '0 16px 48px rgba(0,0,0,.25)',
  sheet: '-8px 0 32px rgba(0,0,0,.18)',
  snackbar: '0 6px 20px rgba(0,0,0,.25)',
  page: '0 2px 12px rgba(0,0,0,.18)',
} as const;

export const layout = {
  topBarHeight: 64,
  navWidth: 264,
  navWidthCollapsed: 68,
  contentMaxWidth: 1440,
  pagePaddingY: 24,
  pagePaddingX: 32,
} as const;

export const fontFamily = {
  latin: "'Inter Variable', Inter, 'IBM Plex Sans Arabic', system-ui, sans-serif",
  arabic: "'IBM Plex Sans Arabic', 'Inter Variable', Inter, system-ui, sans-serif",
} as const;
